import type { ReactNode } from 'react';
import { Fragment } from 'react';

import type { ApiListingLocationType } from '#/api/types/ApiListingLocationType';
import { LegacyTooltip } from '#/components/LegacyTooltip/LegacyTooltip';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ListingLocationLabel } from '#/modules/listing/components/ListingLocationLabel/ListingLocationLabel';
import { ListingLocationTypeLabel } from '#/modules/listing/components/ListingLocationTypeLabel/ListingLocationTypeLabel';
import { ListingTypeLabel } from '#/modules/listing/components/ListingTypeLabel/ListingTypeLabel';
import {
  SearchHitInfoFooterHolder,
  SearchHitInfoHeader,
  SearchHitInfoHeaderHeading,
  SearchHitInfoHeaderOrgName,
  SearchHitInfoHolder,
  SearchHitInfoPipe,
  SearchHitInfoStats,
} from '#/modules/search/components/Hit/Info/SearchHitInfo.styled';
import { SearchHitApplicationStatus } from '#/modules/search/components/Hit/SearchHitApplicationStatus';
import type { DatetimeString } from '#/types/TypedDateString';
import {
  safeUtf8StringSlice,
  utf8StringLength,
} from '#/utils/string/safeUtf8String';

import { SearchInternshipHitLabels } from './SearchInternshipHitInfo.styled';

type Props = {
  application: {
    status: 'ACTIVE' | 'INACTIVE';
    created: DatetimeString;
  } | null;
  isPostedAnonymously: boolean;
  location: string | null;
  locationType: ApiListingLocationType | null;
  listingStats: ReactNode[];
  name: string;
  orgName: string | null;
  orgType: string | null;
  showLabel: boolean;
};

export function SearchInternshipHitInfo({
  application,
  isPostedAnonymously,
  location,
  locationType,
  listingStats,
  name,
  orgName,
  orgType,
  showLabel,
}: Props) {
  const { i18n } = useI18n();

  return (
    <SearchHitInfoHolder>
      {showLabel && (
        <div style={{ marginBottom: 14 }}>
          <ListingTypeLabel listingType="INTERNSHIP" orgType={null} />
        </div>
      )}

      <SearchHitInfoHeader>
        <SearchHitInfoHeaderHeading>
          <div>
            <span data-qa-id="search-result-link">
              {safeUtf8StringSlice(name, 0, 120)}
            </span>
            {utf8StringLength(name) > 120 && <>&hellip;</>}
          </div>

          {isPostedAnonymously && (
            <div
              style={{
                verticalAlign: 'middle',
                display: 'inline-block',
                marginLeft: '4px',
              }}
            >
              <LegacyTooltip width={360}>
                {i18n(
                  `The hiring organization has chosen to publish this listing anonymously to maintain confidentiality during the hiring process. They have provided Idealist with all of their organization’s details and currently meet our posting guidelines.`,
                )}
              </LegacyTooltip>
            </div>
          )}
        </SearchHitInfoHeaderHeading>

        {(orgType === 'CORPORATION' || orgName) && (
          <SearchHitInfoHeaderOrgName>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {orgName}
            </div>
          </SearchHitInfoHeaderOrgName>
        )}
      </SearchHitInfoHeader>

      <SearchInternshipHitLabels>
        {locationType && (
          <ListingLocationTypeLabel
            listingType="INTERNSHIP"
            locationType={locationType}
          />
        )}

        {location && locationType && (
          <ListingLocationLabel
            locationType={locationType}
            location={location}
          />
        )}
      </SearchInternshipHitLabels>

      <SearchHitInfoFooterHolder>
        {listingStats.length > 0 && (
          <SearchHitInfoStats>
            {listingStats.map((info, index) => (
              <Fragment key={index}>
                {index > 0 && <SearchHitInfoPipe />}
                {info}
              </Fragment>
            ))}
          </SearchHitInfoStats>
        )}

        {application && (
          <SearchHitApplicationStatus
            status={application.status}
            created={application.created}
          />
        )}
      </SearchHitInfoFooterHolder>
    </SearchHitInfoHolder>
  );
}
